import { useThree } from '@react-three/fiber';
import { EffectComposerContext } from '@react-three/postprocessing';
import { EffectPass } from 'postprocessing';
import { forwardRef, useContext, useMemo } from 'react';
import { PerspectiveCamera } from 'three';
import { getKawaseHighPassEffectPass, getKawaseStreaksEffectPass } from '../../lib/materials/lightStreakPPEffectPasses';

/**
 * Two-pass Kawase light-streak effect ported from hd_conf. A high-pass isolates gem facet
 * highlights above `threshold` (only possible because gems render `toneMapped:false` against the
 * dark studio HDR, so their highlights blow past 1.0 in the un-tone-mapped HalfFloat composer
 * buffer), then a directional streak blur draws the "star" sparkle. Gate via `enabled` — neo
 * turns it on only when gems are visible (styleMode === 'gems'); the emitting-lines style modes
 * skip it.
 */
export interface KawaseLightStreakProps {
    threshold: number;
    attenuation: number;
    blurPasses: number;
    opacity: number;
    enabled: boolean;
}

export const KawaseLightStreak = forwardRef<EffectPass[], Partial<KawaseLightStreakProps>>(
    ({ threshold = 5, attenuation = 0.9, blurPasses = 4, opacity = 0.25, enabled = false }, fref) => {
        const { scene, camera } = useContext(EffectComposerContext);
        const { width, height } = useThree((state) => state.size);
        const effectPass1 = useMemo(
            () =>
                getKawaseHighPassEffectPass({
                    camera,
                    threshold,
                    opacity,
                }),
            [camera, threshold, opacity],
        );
        const effectPass2 = useMemo(
            () =>
                getKawaseStreaksEffectPass({
                    scene,
                    camera: camera as PerspectiveCamera,
                    width,
                    height,
                    attenuation,
                    blurPasses,
                    enabled,
                }),
            [scene, camera, width, height, attenuation, blurPasses, enabled],
        );

        // TODO when/how to dispose all blurPasses, RT's etc
        return enabled ? (
            <>
                <primitive ref={fref} object={effectPass1} dispose={null} name={'highpass'} />
                <primitive ref={fref} object={effectPass2} dispose={null} name={'streaks'} />
            </>
        ) : (
            <></>
        );
    },
);

KawaseLightStreak.displayName = 'KawaseLightStreak';
